import React, { createContext, useContext, useEffect } from 'react';
import { initSentry, setUserContext, clearUserContext } from '@/lib/sentry';
import { initPostHog, identifyUser, resetUser } from '@/lib/analytics';
import { initSecurity } from '@/lib/security';
import { useAuth } from './AuthProvider';
import { useGlobalErrorHandler } from '@/hooks/useGlobalErrorHandler';

interface ObservabilityContextType {
  isInitialized: boolean;
}

const ObservabilityContext = createContext<ObservabilityContextType>({
  isInitialized: false
});

export const useObservability = () => {
  const context = useContext(ObservabilityContext);
  if (!context) {
    throw new Error('useObservability must be used within ObservabilityProvider');
  }
  return context;
};

interface ObservabilityProviderProps {
  children: React.ReactNode;
}

export const ObservabilityProvider: React.FC<ObservabilityProviderProps> = ({ children }) => {
  const { user, userRole, userProfile } = useAuth();
  
  // Initialize global error handling
  useGlobalErrorHandler();

  // Initialize observability tools
  useEffect(() => {
    // Initialize Sentry
    initSentry();
    
    // Initialize PostHog
    initPostHog();
    
    // Initialize security checks
    initSecurity();
    
    console.log('🔍 Observability and security tools initialized');
  }, []);

  // Handle user context changes
  useEffect(() => {
    if (user && userProfile) {
      // Set user context in Sentry
      setUserContext({
        id: user.id,
        email: user.email,
        role: userRole || 'user'
      });

      // Identify user in PostHog
      identifyUser(user.id, {
        email: user.email,
        role: userRole,
        name: userProfile.full_name,
        first_name: userProfile.first_name,
        last_name: userProfile.last_name
      });

      console.log('👤 User context set for observability');
    } else {
      // Clear user context
      clearUserContext();
      resetUser();
      
      console.log('👤 User context cleared from observability');
    }
  }, [user, userRole, userProfile]);

  const value: ObservabilityContextType = {
    isInitialized: true
  };

  return (
    <ObservabilityContext.Provider value={value}>
      {children}
    </ObservabilityContext.Provider>
  );
};